'use client'

import React, { ComponentRef, createContext, ReactNode, useContext } from 'react'
import Link, { LinkProps } from 'next/link'
import { IconDefinition } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'
import { cn } from '@utils/cn'

interface MenuContextType {
  offset?: number
  placement?: string
}

const MenuContext = createContext<MenuContextType>({})

interface MenuProps {
  children: ReactNode
  open?: boolean
  handler?: () => void
  offset?: number
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end'
}

const Menu = ({ children, open, handler, offset = 5, placement = 'bottom', ...props }: MenuProps) => {
  const [isOpen, setIsOpen] = React.useState(open ?? false)

  React.useEffect(() => {
    if (open !== undefined) {
      setIsOpen(open)
    }
  }, [open])

  const handleOpenChange = (newOpen: boolean) => {
    setIsOpen(newOpen)
    if (handler) {
      handler()
    }
  }

  return (
    <MenuContext.Provider value={{ offset, placement }}>
      <DropdownMenuPrimitive.Root open={isOpen} onOpenChange={handleOpenChange} {...props}>
        {children}
      </DropdownMenuPrimitive.Root>
    </MenuContext.Provider>
  )
}

const MenuHandler = React.forwardRef<ComponentRef<typeof DropdownMenuPrimitive.Trigger>, React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Trigger>>(
  ({ className, children, ...props }, ref) => {
    // Если есть children, используем asChild чтобы передать пропсы дочернему элементу
    // Это предотвращает создание вложенных кнопок
    if (children) {
      // Клонируем дочерний элемент с объединенным className
      const child = React.Children.only(children) as React.ReactElement<{ className?: string }>
      const childClassName = child.props?.className
      return (
        <DropdownMenuPrimitive.Trigger ref={ref} asChild {...props}>
          {React.cloneElement(child, {
            className: cn('flex rounded-full text-sm ml-2 p-2 focus:outline-none', className, childClassName),
          } as { className?: string })}
        </DropdownMenuPrimitive.Trigger>
      )
    }
    return <DropdownMenuPrimitive.Trigger ref={ref} className={cn('flex rounded-full text-sm ml-2 p-2 focus:outline-none', className)} {...props} />
  }
)
MenuHandler.displayName = 'MenuHandler'

const MenuList = React.forwardRef<
  ComponentRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content> & { offset?: number; placement?: string }
>(({ className, offset: offsetProp, placement: placementProp, ...props }, ref) => {
  const context = useContext(MenuContext)
  const offset = offsetProp ?? context.offset ?? 5
  const placement = placementProp ?? context.placement ?? 'bottom'

  const getPlacement = (): {
    side: 'top' | 'bottom' | 'left' | 'right'
    align: 'center' | 'start' | 'end'
  } => {
    if (placement?.includes('-')) {
      const [side, align] = placement.split('-')
      return {
        side: side as 'top' | 'bottom' | 'left' | 'right',
        align: (align === 'start' ? 'start' : align === 'end' ? 'end' : 'center') as 'center' | 'start' | 'end',
      }
    }
    return {
      side: (placement || 'bottom') as 'top' | 'bottom' | 'left' | 'right',
      align: 'center' as const,
    }
  }

  const { side, align } = getPlacement()

  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        ref={ref}
        side={side}
        align={align}
        sideOffset={offset}
        className={cn(
          'p-0 shadow-lg gap-0 min-w-[220px] bg-white rounded-md border border-slate-200 z-50',
          'max-h-[320px] overflow-y-auto overflow-x-hidden',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          className
        )}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  )
})
MenuList.displayName = 'MenuList'

const menuItemStyles = cn(
  'p-0 text-sm rounded-none',
  'hover:bg-gray-100 hover:text-slate-900',
  'focus:bg-gray-100 focus:text-slate-900',
  'outline-none cursor-pointer'
)

const MenuItem = React.forwardRef<ComponentRef<typeof DropdownMenuPrimitive.Item>, React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item>>(
  ({ className, children, ...props }, ref) => {
    return (
      <DropdownMenuPrimitive.Item ref={ref} className={cn(menuItemStyles, className)} {...props}>
        {children}
      </DropdownMenuPrimitive.Item>
    )
  }
)
MenuItem.displayName = 'MenuItem'

const MenuIcon = ({ icon }: { icon: IconDefinition }) => {
  return <FontAwesomeIcon icon={icon} className="mr-4 w-4 h-4 text-slate-400 group-hover:text-slate-500" />
}

const linkStyle = cn('flex items-center px-4 py-3.5 m-0 text-sm text-slate-700')

interface MenuLinkPropsType extends LinkProps {
  className?: string
  children: ReactNode
  icon?: IconDefinition
}

const MenuLink = ({ href, children, icon, className = '', ...props }: MenuLinkPropsType) => {
  return (
    <Link href={href} className={cn(linkStyle, className)} {...props}>
      {icon ? <MenuIcon icon={icon} /> : null}
      {children}
    </Link>
  )
}

interface MenuSpanPropsType extends React.HTMLAttributes<HTMLDivElement> {
  className?: string
  children: ReactNode
  icon?: IconDefinition
}

const MenuSpan = ({ children, icon, className = '', ...props }: MenuSpanPropsType) => {
  return (
    <div className={cn(linkStyle, className)} {...props}>
      {icon ? <MenuIcon icon={icon} /> : null}
      {children}
    </div>
  )
}

export { Menu, MenuHandler, MenuIcon, MenuItem, MenuLink, MenuList, MenuSpan }
